<template>
  <div
    v-if="moreThreeSlides"
    :class="wrappClass"
    class="articles-carousel-wrapper"
  >
    <div
      v-if="title"
      class="articles-carousel-title"
    >
      {{ title }}
    </div>
    <Swiper
      class="articles-carousel"
      container-class="articles-carousel-container"
      show-arrows-on-hover
    >
      <ArticlesCarouselSlide
        v-for="(article, index) of articles"
        :key="article.id"
        :article="article"
        :serial-number="index + 1"
        class="articles-carousel__slide"
        @on-click-item="$emit('onClickItem', article, index)"
      />
      <template #left-arrow>
        <ScrollButton class="left-arrow" />
      </template>
      <template #right-arrow>
        <ScrollButton class="right-arrow" />
      </template>
    </Swiper>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import ArticlesCarouselSlide from '~/components/ArticlesCarousel/ArticlesCarouselSlide.vue';
  import type { TArticle } from '~/types/common';
  import ScrollButton from '~/components/BaseComponents/ScrollButton.vue';
  import Swiper from '@devhacker/shared/components/Swiper.vue';

  export default defineNuxtComponent({
    name: 'ArticlesCarousel',

    components: {
      Swiper,
      ArticlesCarouselSlide,
      ScrollButton,
    },

    props: {
      articles: {
        type: Array as PropType<TArticle[]>,
        required: true,
      },

      area: {
        type: String as PropType<string | undefined>,
        required: true,
      },

      title: {
        type: String as PropType<string>,
        default: '',
      },
    },

    computed: {
      wrappClass(): Record<string, boolean> {
        const areas: Array<string> = [
          'above-comments',
          'below-comments',
          'home',
          'after-first-paragraph',
          'post-dynamic',
        ];

        return {
          [`articles-carousel-wrapper--${this.area}`]: areas.includes(this.area ? this.area : ''),
        };
      },

      moreThreeSlides(): boolean {
        return this.articles.length > 3;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .articles-carousel-wrapper {
    // padding: $spacing-6 $spacing-4;
    padding: $spacing-6 $spacing-0;
    @include tablet {
      padding: 0;
    }

    &--home {
      padding: 0;

      .articles-carousel {
        max-width: 828px;
        padding-left: $spacing-4;
        padding-right: $spacing-4;
        @include tablet {
          padding-left: 0;
          padding-right: 0;
        }
      }

      .articles-carousel-title {
        color: #fff;
        padding-left: $spacing-4;
        @include tablet {
          padding-left: 0;
          color: $mainBlack;
        }
      }
    }

    &--above-comments,
    &--below-comments {
      padding: $spacing-0;

      .articles-carousel {
        @include tablet {
          max-width: 828px;
        }
      }
    }
  }

  .articles-carousel {
    margin: 0;
    position: relative;

    @include tablet {
      padding: 0;
    }

    :deep(.articles-carousel-container) {
      gap: 12px;
    }

    &__slide {
      background: radial-gradient(74.97% 74.97% at 50% 25.03%, #e8aefc 0%, #76dddc 100%);

      &:nth-child(odd) {
        background: radial-gradient(81.6% 81.6% at 90.48% 97.64%, #f2e856 0%, #76dddc 100%);
      }
    }
  }

  .articles-carousel-title {
    font-family: $headline-font--1;
    font-weight: 400;
    font-size: 18px;
    margin-bottom: 8px;
    color: $mainBlack;
    line-height: 28px;
    @include big-tablet {
      font-size: 22px;
      line-height: 32px;
    }
  }

  .right-arrow,
  .left-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .left-arrow {
    transform: scaleX(-1);
  }
</style>
